import React from 'react'
import { useTheme } from '@/hooks/useTheme'
import { useDeviceDetection } from '@/hooks/useDeviceDetection'
import { useReportIngresos } from '@/hooks/useReports'
import { FiltrosReporte } from '@/services/reportService'
import ReportCard from './ReportCard'
import ReportTable from './ReportTable'
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

interface IngresosReportProps {
  filtros: FiltrosReporte
  onRefresh?: () => void
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-96 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-center">
            <p className="text-gray-600 font-medium">Error al renderizar el gráfico</p>
            <p className="text-sm text-gray-500 mt-2">Intenta actualizar la página</p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const IngresosReport: React.FC<IngresosReportProps> = ({ filtros, onRefresh }) => {
  const { colors } = useTheme()
  const { isMobile } = useDeviceDetection()
  const { data: reportData, isLoading, error, refetch } = useReportIngresos(filtros)

  const handleRefresh = () => {
    refetch()
    onRefresh?.()
  }

  const getErrorMessage = () => {
    if (!error) return undefined
    const errorString = error instanceof Error ? error.message : String(error)
    return errorString
  }

  const handleExport = () => {
    if (!reportData?.datos) return

    const csv = [
      ['Tipo', 'Reservas', 'Ingresos Totales', 'Ingresos Promedio'].join(','),
      ...reportData.datos.map((d) =>
        [d.tipo, d.reservas, d.ingresos_totales.toFixed(2), d.ingresos_promedio.toFixed(2)].join(',')
      )
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ingresos-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const chartData = reportData?.datos?.map((item, index) => {
    const totales = Number(item.ingresos_totales) || 0
    const promedio = Number(item.ingresos_promedio) || 0
    // Usar índice como clave única para evitar duplicados en tipos que empiezan igual
    return {
      tipo: item.tipo, // Nombre completo para tooltip y tabla
      tipoCorto: `T${index + 1}`, // Etiqueta única basada en índice (T1, T2, T3, etc.)
      reservas: Number(item.reservas) || 0,
      ingresos_totales: Number.isFinite(totales) ? totales : 0,
      ingresos_promedio: Number.isFinite(promedio) ? promedio : 0
    }
  }) || []

  const tableColumns = [
    { key: 'tipo' as const, label: 'Tipo de espacio' },
    { key: 'reservas' as const, label: 'Total reservas' },
    {
      key: 'ingresos_totales' as const,
      label: 'Ingresos totales',
      render: (value: number) => {
        const validValue = Number.isFinite(value) ? value : 0
        return `$${validValue.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`
      }
    },
    {
      key: 'ingresos_promedio' as const,
      label: 'Ingresos promedio',
      render: (value: number) => {
        const validValue = Number.isFinite(value) ? value : 0
        return `$${validValue.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`
      }
    }
  ]

  return (
    <ReportCard
      title="Ingresos por Tipo"
      subtitle="Análisis de ingresos generados por tipo de espacio"
      loading={isLoading}
      error={getErrorMessage()}
      onExport={handleExport}
      chartComponent={
        <ErrorBoundary>
          <div style={{ width: '100%', height: '100%', minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={isMobile ? 400 : 500}>
              <ComposedChart
                data={chartData}
                margin={isMobile
                  ? { top: 5, right: 10, bottom: 40, left: 10 }
                  : { top: 5, right: 120, bottom: 40, left: 130 }
                }
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="tipoCorto"
                  angle={0}
                  height={30}
                  tick={false}
                />
                <YAxis
                  yAxisId="left"
                  label={isMobile ? undefined : { value: 'Ingresos Totales ($)', angle: -90, position: 'insideLeft', offset: 0, dx: -50 }}
                  tick={{
                    fontSize: isMobile ? 10 : 12,
                    formatter: (value: number) => isMobile
                      ? `$${(value / 1000).toFixed(0)}k`
                      : value.toLocaleString('es-CL', { maximumFractionDigits: 0 })
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={isMobile ? undefined : { value: 'Ingresos Promedio ($)', angle: 90, position: 'insideRight', offset: 0, dx: 50 }}
                  tick={{
                    fontSize: isMobile ? 10 : 12,
                    formatter: (value: number) => isMobile
                      ? `$${(value / 1000).toFixed(0)}k`
                      : value.toLocaleString('es-CL', { maximumFractionDigits: 0 })
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    color: colors.primaryDark,
                    borderRadius: '8px',
                    border: `2px solid ${colors.primary}`,
                    padding: '12px',
                    fontSize: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value: any, name: string, props: any) => {
                    if (typeof value === 'number') {
                      const formattedValue = `$${value.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`

                      // Agregar descripción adicional según el tipo de serie
                      if (name === 'Ingresos Totales') {
                        return [formattedValue, `${name} (barra)`]
                      } else if (name === 'Ingresos Promedio') {
                        return [formattedValue, `${name} por reserva (línea)`]
                      }
                      return [formattedValue, name]
                    }
                    return [value, name]
                  }}
                  labelFormatter={(label: any) => {
                    // Buscar el item del data para mostrar el nombre completo del tipo
                    const item = chartData.find(d => d.tipoCorto === label)
                    return item ? `Tipo: ${item.tipo}` : `Tipo: ${label}`
                  }}
                  wrapperStyle={{ outline: 'none' }}
                />
                <Legend
                  layout={isMobile ? 'horizontal' : 'vertical'}
                  align={isMobile ? 'center' : 'right'}
                  verticalAlign={isMobile ? 'bottom' : 'middle'}
                  wrapperStyle={{ paddingLeft: isMobile ? '0' : '20px', fontSize: isMobile ? '10px' : '12px' }}
                />
                <Bar
                  yAxisId="left"
                  dataKey="ingresos_totales"
                  fill={colors.primary}
                  name="Ingresos Totales"
                  radius={[8, 8, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="linear"
                  dataKey="ingresos_promedio"
                  stroke={colors.primaryDark}
                  strokeWidth={3}
                  name="Ingresos Promedio"
                  dot={{
                    fill: colors.primaryDark,
                    r: 5
                  }}
                  activeDot={{
                    r: 7,
                    fill: colors.primaryDark
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </ErrorBoundary>
      }
      tableComponent={<ReportTable columns={tableColumns} data={chartData} />}
    >
      {reportData?.resumen && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Ingresos totales</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              ${reportData.resumen.ingresos_totales.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
            </p>
          </div>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Ingresos promedio</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              ${reportData.resumen.ingresos_promedio.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
            </p>
          </div>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Cantidad reservas</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              {reportData.resumen.cantidad_reservas}
            </p>
          </div>
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: `${colors.primary}15`,
              borderLeft: `4px solid ${colors.primary}`
            }}
          >
            <p className="text-xs text-gray-600 mb-1">Ingresos/reserva</p>
            <p className="text-2xl font-bold" style={{ color: colors.primary }}>
              ${reportData.resumen.ingresos_por_reserva.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
            </p>
          </div>
        </div>
      )}
    </ReportCard>
  )
}

export default IngresosReport
